import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RenderUnit, { snapshot } from '../helpers/figures/RenderUnit';
import { countDiffPixels, fetchCurrent, saveFigure, toDrawing } from '../helpers/figures/figureApi';
import { DRAWING_CATALOG, getDrawingUrl } from '../../config/drawings';

// 学習者に見せるデッサン画像（public/dessin/）を作り直す開発用ページ（/drawing-builder）。
// 学習者には案内しない。npm start と flask run の両方が動いている状態で開く。
//   1. 今の実装でモデルを描画し、/reverse でデッサン風にした「新しい画像」を1枚ずつ作る
//   2. public/dessin/ に置いてある「今の画像」と画素単位で比べ、変わったものに印を付ける
//   3. チェックした画像だけを /capture（dest=dessin）で上書きする
// URLの指定：?models=model3,model8 … そのモデル（とモチーフ）だけ作る
//            ?save=changed         … 作り終えたら変更・新規の画像を自動で保存する（ヘッドレス実行用）
const STATUS_LABEL = {
  pending: '待機中', same: '変更なし', changed: '変更あり', new: '新規', saved: '保存済み', error: 'エラー',
};
const STATUS_COLOR = { changed: '#c2410c', new: '#1d4ed8', saved: '#15803d', error: '#b91c1c' };

const readParams = () => {
  const params = new URLSearchParams(window.location.search);
  const models = (params.get('models') || '').split(',').map((s) => s.trim()).filter(Boolean);
  return { models, autoSave: params.get('save') === 'changed' };
};

export default function DrawingBuilder() {
  const { models, autoSave } = useMemo(readParams, []);
  const items = useMemo(() => DRAWING_CATALOG.filter((it) =>
    !models.length || models.includes(it.glbPath) || it.level === 'motif'), [models]);
  const [rows, setRows] = useState(() => items.map((it) => ({ ...it, status: 'pending' })));
  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const done = index >= items.length;

  const patch = (i, values) => setRows((prev) => prev.map((r, k) => (k === i ? { ...r, ...values } : r)));

  const handleReady = useCallback(async (three) => {
    const item = items[index];
    try {
      const blob = await toDrawing(await snapshot(three));
      const current = await fetchCurrent(getDrawingUrl(item.glbPath, item.level));
      const diff = current ? await countDiffPixels(blob, current) : null;
      const status = current == null ? 'new' : diff > 0 ? 'changed' : 'same';
      patch(index, {
        blob, diff, status, checked: status !== 'same',
        newUrl: URL.createObjectURL(blob),
        currentUrl: current ? URL.createObjectURL(current) : null,
      });
    } catch (err) {
      patch(index, { status: 'error', message: err.message });
    } finally {
      setIndex((i) => i + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, items]);

  const saveChecked = useCallback(async () => {
    setSaving(true);
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (!r.checked || !r.blob) continue;
      try {
        await saveFigure(r.blob, r.fileName, 'dessin');
        patch(i, { status: 'saved', checked: false, currentUrl: r.newUrl, diff: 0 });
      } catch (err) {
        patch(i, { status: 'error', message: err.message });
      }
    }
    setSaving(false);
  }, [rows]);

  useEffect(() => {
    if (done && autoSave) saveChecked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const count = (s) => rows.filter((r) => r.status === s).length;
  const item = items[index];

  return (
    <div style={{ padding: 16, fontFamily: 'sans-serif' }}>
      <h1 style={{ fontSize: 18 }}>デッサン画像の作り直し（開発用）</h1>
      <p style={{ fontSize: 13 }}>
        {done
          ? `比較完了：変更あり ${count('changed')}／新規 ${count('new')}／変更なし ${count('same')}／保存済み ${count('saved')}／エラー ${count('error')}`
          : `${index + 1} / ${items.length} 枚目を作成中：${item.fileName}`}
      </p>
      <p style={{ fontSize: 12, color: '#555' }}>
        モデルの形を変えたら、変更ありの画像を確かめてから保存してください。保存すると学習者の画面に表示されるデッサンが置き換わります（プレテストで見せた画像と変わる点に注意）。
      </p>
      <button type="button" onClick={saveChecked} disabled={!done || saving || !rows.some((r) => r.checked)}>
        {saving ? '保存中…' : 'チェックした画像を public/dessin/ に保存'}
      </button>
      {done && <div id="builder-done" data-saved={count('saved')} />}
      {!done && (
        <div style={{ position: 'absolute', left: -10000 }}>
          <RenderUnit key={item.fileName} glbPath={item.glbPath} shearValue={item.shearValue} onReady={handleReady} />
        </div>
      )}
      <table style={{ marginTop: 12, borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr><th>保存</th><th>ファイル</th><th>状態</th><th>今の画像</th><th>新しい画像</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.fileName} style={{ borderTop: '1px solid #ddd' }}>
              <td><input type="checkbox" checked={!!r.checked} disabled={!r.blob} onChange={(e) => patch(i, { checked: e.target.checked })} /></td>
              <td>{r.fileName}</td>
              <td style={{ color: STATUS_COLOR[r.status] || '#555' }}>
                {STATUS_LABEL[r.status]}{r.diff ? `（${r.diff}画素）` : ''}{r.message ? `：${r.message}` : ''}
              </td>
              <td>{r.currentUrl ? <img src={r.currentUrl} alt="今の画像" width={240} /> : '—'}</td>
              <td>{r.newUrl ? <img src={r.newUrl} alt="新しい画像" width={240} /> : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
